import {
    DownloadButtonData,
    IntegrationOption,
} from "@nova/component-export-download-buttons-hover"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { AGOLSettings } from "@nova/db-table-integration-authorizations"
import { IntegrationType } from "@nova/schemas"
import { useIntegrationAuthorizations } from "features/data-management/integrations/useIntegrations"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { useExportToAGOL } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/useExportToAGOL"
import {
    selectActiveWorkspaceId,
    selectWorkspaceDateFormat,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { selectIconById } from "features/entity-caching/icons/selectors"
import { buildOAuthPopup, isAuthExpired } from "integrations/oauth/oauthHelpers"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectLastSelectedLayerId } from "panels/content/side-panel/slice.new"
import { useState } from "react"
import { setGlobalNotificationContext } from "user/profile/slice"
import { getDate } from "../module"
import { TimeFormat } from "@nova/format"
import { useExportToCalTopo } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/caltopo/useExportToCalTopo"
import { useExportToTAK } from "features/data-management/workspace/workspace/body/settings-and-collaborators/integrations/tak/useExportToTAK"
import { IntegrationExportEntity } from "@nova/constants"
import { selectActivatedFireId } from "features/map/control/selectors"

export const useIconDownloadButtonData = (): DownloadButtonData => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const fireId = useAppSelector(selectActivatedFireId)
    const lastSelectedLayerId = useAppSelector(selectLastSelectedLayerId) || ""
    const icon = useAppSelector(selectIconById(lastSelectedLayerId))

    const dispatch = useAppDispatch()

    const { data: agolAuths, invalidateAuthorizationsCache: invalidateAGOL } =
        useIntegrationAuthorizations({
            authType: "oauth",
            integration: IntegrationType.agol,
            workspaceId: workspaceId,
        })
    const { exportToAGOL } = useExportToAGOL()
    const [agolExportError, setAGOLExportError] = useState<string>("Failed to export")

    const { data: takAuths, invalidateAuthorizationsCache: invalidateTAK } =
        useIntegrationAuthorizations({
            authType: "certificate",
            integration: IntegrationType.tak,
            workspaceId: workspaceId,
        })
    const { data: caltopoAuths, invalidateAuthorizationsCache: invalidateCalTopo } =
        useIntegrationAuthorizations({
            authType: "caltopo",
            integration: IntegrationType.caltopo,
            workspaceId: workspaceId,
        })
    const { exportToTAK } = useExportToTAK()
    const { exportToCalTopo } = useExportToCalTopo()

    const integrationOptions: Record<string, IntegrationOption> = {}

    if (agolAuths?.at(0) && fireId && workspaceId) {
        const auth = agolAuths.at(0)
        const settings = auth?.settings as AGOLSettings
        const lastExport = auth?.lastExport?.[icon.id]

        integrationOptions[`Send to ${settings.selectedWebMap?.title ?? "User Content"}`] = {
            callback: async () => {
                if (!auth) return
                if (!icon) {
                    setAGOLExportError("Internal error occured")
                }

                if (isAuthExpired(auth)) {
                    try {
                        await buildOAuthPopup(IntegrationType.agol, workspaceId)
                    } catch (e) {
                        setAGOLExportError("Failed to authenticate")
                        throw e
                    }
                    await invalidateAGOL()
                    dispatch(
                        setGlobalNotificationContext({
                            message: `Connected to ${IntegrationType.agol.toUpperCase()}.`,
                            status: GlobalNotificationStatus.success,
                            timeoutInMs: 3000,
                        })
                    )
                }

                return exportToAGOL({
                    projectId: fireId,
                    workspaceId,
                    entities: [{ id: lastSelectedLayerId, type: IntegrationExportEntity.Icon }],
                })
                    .then(() => {
                        invalidateAGOL()
                    })
                    .catch((e) => {
                        setAGOLExportError("Failed to export")
                        throw e
                    })
            },
            successMessage: "Success",
            failureMessage: agolExportError,
            key: "agol",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }

    if (takAuths?.at(0) && fireId && workspaceId) {
        const auth = takAuths.at(0)
        const lastExport = auth?.lastExport?.[icon.id]

        integrationOptions["Send to TAK"] = {
            callback: async () => {
                if (!auth) return
                if (!icon) {
                    throw "No flight selected"
                }

                return exportToTAK({
                    projectId: fireId,
                    workspaceId,
                    entities: [{ id: lastSelectedLayerId, type: IntegrationExportEntity.Icon }],
                }).then(() => {
                    invalidateTAK()
                })
            },
            successMessage: "Success",
            failureMessage: "Failed",
            key: "tak",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }

    if (caltopoAuths?.at(0) && fireId && workspaceId) {
        const auth = caltopoAuths.at(0)
        const lastExport = auth?.lastExport?.[icon.id]

        integrationOptions["Send to CalTopo"] = {
            callback: async () => {
                if (!auth) return

                return await exportToCalTopo({
                    entities: [{ id: lastSelectedLayerId, type: IntegrationExportEntity.Icon }],
                    projectId: fireId,
                    workspaceId,
                }).then(() => {
                    invalidateCalTopo()
                })
            },
            successMessage: "Success",
            failureMessage: "Failed",
            key: "caltopo",
            ExtraInfo: () => {
                const { fullName } = useFullnameForUserId(lastExport?.userId)
                const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)

                return lastExport ? (
                    <div className="flex flex-col gap-2 mt-2 mb-2.5 text-gray-50 min-h-fit">
                        <small className="flex justify-between">
                            <span>Last Updated: </span>
                            <span className="mono text-gray-300">
                                {getDate(
                                    lastExport.time / 1000,
                                    workspaceDateFormat,
                                    TimeFormat.TwentyFourHour
                                )}
                            </span>
                        </small>
                        {fullName && (
                            <small className="flex justify-between">
                                Updated By: <span className="text-gray-300">{fullName}</span>
                            </small>
                        )}
                    </div>
                ) : (
                    <div></div>
                )
            },
        }
    }

    return { exportOptions: {}, integrationOptions }
}
